import { persistentAtom } from '@nanostores/persistent'
import { computed, type Store } from 'nanostores'
import type { UsageEntry } from '$lib/types/ai'

// Re-export for tests
export type { UsageEntry }

// Usage tracking types
export interface QuotaLimits {
  daily: {
    tokens: number
    requests: number
    cost: number
  }
  monthly: {
    tokens: number
    requests: number
    cost: number
  }
  enabled: boolean
}

export interface UsageAlert {
  id: string
  type: 'warning' | 'critical' | 'exceeded'
  threshold: number
  message: string
  timestamp: number
  acknowledged: boolean
}

export interface ModelUsageStats {
  model: string
  provider: string
  totalRequests: number
  totalTokens: number
  totalCost: number
  avgResponseTime: number
  successRate: number
  lastUsed: number
  failureCount: number
}

export interface PeriodUsage {
  period: string  // 'YYYY-MM-DD' for daily, 'YYYY-MM' for monthly
  totalRequests: number
  totalTokens: number
  totalCost: number
  avgResponseTime: number
  models: Record<string, ModelUsageStats>
  alerts: UsageAlert[]
}

export interface UsageStoreState {
  // Current tracking
  isTracking: boolean
  currentSession: {
    startTime: number
    requests: number
    tokens: number
    cost: number
  }
  
  // Historical data
  dailyUsage: Record<string, PeriodUsage>
  monthlyUsage: Record<string, PeriodUsage>
  
  // Quota management
  quotaLimits: QuotaLimits
  activeAlerts: UsageAlert[]
  
  // Performance metrics
  performanceMetrics: {
    avgSuccessRate: number
    avgResponseTime: number
    totalFailures: number
    lastCalculated: number
  }
  
  // Configuration
  trackingEnabled: boolean
  alertsEnabled: boolean
  retentionDays: number  // How long to keep usage data
}

// Default usage store state
const defaultUsageState: UsageStoreState = {
  isTracking: false,
  currentSession: {
    startTime: Date.now(),
    requests: 0,
    tokens: 0,
    cost: 0
  },
  dailyUsage: {},
  monthlyUsage: {},
  quotaLimits: {
    daily: {
      tokens: 50000,    // 50K tokens per day
      requests: 100,    // 100 requests per day
      cost: 5.0        // $5 per day
    },
    monthly: {
      tokens: 1000000,  // 1M tokens per month
      requests: 2000,   // 2000 requests per month
      cost: 100.0      // $100 per month
    },
    enabled: false  // Disabled by default
  },
  activeAlerts: [],
  performanceMetrics: {
    avgSuccessRate: 0,
    avgResponseTime: 0,
    totalFailures: 0,
    lastCalculated: 0
  },
  trackingEnabled: true,
  alertsEnabled: true,
  retentionDays: 90  // 3 months of data retention
}

// Test environment handling
const isTestEnvironment = typeof process !== 'undefined' && process.env?.VITEST

export const usageStore = isTestEnvironment
  ? (() => {
      let store = { ...defaultUsageState }
      const listeners = new Set<(value: UsageStoreState) => void>()
      
      return {
        get: () => store,
        set: (value: UsageStoreState) => {
          store = value
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem('korean-grammar:usage', JSON.stringify(value))
          }
          listeners.forEach(fn => fn(value))
        },
        subscribe: (fn: (value: UsageStoreState) => void) => {
          listeners.add(fn)
          fn(store)
          return () => listeners.delete(fn)
        }
      }
    })()
  : persistentAtom<UsageStoreState>(
      'korean-grammar:usage',
      defaultUsageState,
      {
        encode: JSON.stringify,
        decode: (value: string) => {
          try {
            const parsed = JSON.parse(value)
            return {
              ...defaultUsageState,
              ...parsed,
              dailyUsage: parsed.dailyUsage || {},
              monthlyUsage: parsed.monthlyUsage || {},
              activeAlerts: parsed.activeAlerts || [],
              currentSession: {
                ...defaultUsageState.currentSession,
                ...parsed.currentSession
              },
              quotaLimits: {
                ...defaultUsageState.quotaLimits,
                ...parsed.quotaLimits
              },
              performanceMetrics: {
                ...defaultUsageState.performanceMetrics,
                ...parsed.performanceMetrics
              }
            }
          } catch {
            return defaultUsageState
          }
        }
      }
    )

// Computed stores for easier access
export const currentSessionStore = computed(
  usageStore as Store<UsageStoreState>,
  (state: UsageStoreState) => state.currentSession
)

export const activeAlertsStore = computed(
  usageStore as Store<UsageStoreState>,
  (state: UsageStoreState) => state.activeAlerts.filter(alert => !alert.acknowledged)
)

export const dailyUsageStore = computed(
  usageStore as Store<UsageStoreState>,
  (state: UsageStoreState) => state.dailyUsage
)

export const monthlyUsageStore = computed(
  usageStore as Store<UsageStoreState>,
  (state: UsageStoreState) => state.monthlyUsage
)

export const quotaLimitsStore = computed(
  usageStore as Store<UsageStoreState>,
  (state: UsageStoreState) => state.quotaLimits
)

// Utility functions
function getCurrentDateString(): string {
  return new Date().toISOString().slice(0, 10) // YYYY-MM-DD
}

function getCurrentMonthString(): string {
  return new Date().toISOString().slice(0, 7) // YYYY-MM
}

function generateAlertId(): string {
  return `alert_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

function createAlert(
  type: 'warning' | 'critical' | 'exceeded',
  threshold: number,
  current: number,
  metric: string,
  period: 'daily' | 'monthly'
): UsageAlert {
  const percentage = Math.round((current / threshold) * 100)
  
  const messages = {
    warning: `${period} ${metric} usage is at ${percentage}% of limit (${current}/${threshold})`,
    critical: `${period} ${metric} usage is critically high at ${percentage}% of limit (${current}/${threshold})`,
    exceeded: `${period} ${metric} limit exceeded! Current: ${current}, Limit: ${threshold}`
  }
  
  return {
    id: generateAlertId(),
    type,
    threshold,
    message: messages[type],
    timestamp: Date.now(),
    acknowledged: false
  }
}

// Usage store actions
export const usageActions = {
  /**
   * Start tracking a new session
   */
  startSession() {
    const state = usageStore.get()
    usageStore.set({
      ...state,
      isTracking: true,
      currentSession: {
        startTime: Date.now(),
        requests: 0,
        tokens: 0,
        cost: 0
      }
    })
  },

  /**
   * Stop tracking current session
   */
  stopSession() {
    const state = usageStore.get()
    usageStore.set({
      ...state,
      isTracking: false
    })
  },

  /**
   * Record usage entry after AI API call
   */
  recordUsage(entry: UsageEntry) {
    const state = usageStore.get()
    if (!state.trackingEnabled) return

    const dateStr = getCurrentDateString()
    const monthStr = getCurrentMonthString()
    
    // Update current session
    const updatedSession = {
      ...state.currentSession,
      requests: state.currentSession.requests + 1,
      tokens: state.currentSession.tokens + entry.tokenUsage.total,
      cost: state.currentSession.cost + (entry.tokenUsage.cost || 0)
    }
    
    // Update daily usage
    const dailyUsage = { ...state.dailyUsage }
    if (!dailyUsage[dateStr]) {
      dailyUsage[dateStr] = {
        period: dateStr,
        totalRequests: 0,
        totalTokens: 0,
        totalCost: 0,
        avgResponseTime: 0,
        models: {},
        alerts: []
      }
    }
    
    const dailyData = dailyUsage[dateStr]
    dailyData.totalRequests++
    dailyData.totalTokens += entry.tokenUsage.total
    dailyData.totalCost += entry.tokenUsage.cost || 0
    dailyData.avgResponseTime = ((dailyData.avgResponseTime * (dailyData.totalRequests - 1)) + entry.responseTime) / dailyData.totalRequests
    
    // Update model-specific stats for daily
    if (!dailyData.models[entry.model]) {
      dailyData.models[entry.model] = {
        model: entry.model,
        provider: entry.provider,
        totalRequests: 0,
        totalTokens: 0,
        totalCost: 0,
        avgResponseTime: 0,
        successRate: 0,
        lastUsed: 0,
        failureCount: 0
      }
    }
    
    const dailyModelStats = dailyData.models[entry.model]
    dailyModelStats.totalRequests++
    dailyModelStats.totalTokens += entry.tokenUsage.total
    dailyModelStats.totalCost += entry.tokenUsage.cost || 0
    dailyModelStats.avgResponseTime = ((dailyModelStats.avgResponseTime * (dailyModelStats.totalRequests - 1)) + entry.responseTime) / dailyModelStats.totalRequests
    dailyModelStats.lastUsed = entry.timestamp
    
    if (!entry.success) {
      dailyModelStats.failureCount++
    }
    dailyModelStats.successRate = ((dailyModelStats.totalRequests - dailyModelStats.failureCount) / dailyModelStats.totalRequests) * 100
    
    // Update monthly usage (similar structure)
    const monthlyUsage = { ...state.monthlyUsage }
    if (!monthlyUsage[monthStr]) {
      monthlyUsage[monthStr] = {
        period: monthStr,
        totalRequests: 0,
        totalTokens: 0,
        totalCost: 0,
        avgResponseTime: 0,
        models: {},
        alerts: []
      }
    }
    
    const monthlyData = monthlyUsage[monthStr]
    monthlyData.totalRequests++
    monthlyData.totalTokens += entry.tokenUsage.total
    monthlyData.totalCost += entry.tokenUsage.cost || 0
    monthlyData.avgResponseTime = ((monthlyData.avgResponseTime * (monthlyData.totalRequests - 1)) + entry.responseTime) / monthlyData.totalRequests
    
    // Update model stats for monthly
    if (!monthlyData.models[entry.model]) {
      monthlyData.models[entry.model] = {
        model: entry.model,
        provider: entry.provider,
        totalRequests: 0,
        totalTokens: 0,
        totalCost: 0,
        avgResponseTime: 0,
        successRate: 0,
        lastUsed: 0,
        failureCount: 0
      }
    }
    
    const monthlyModelStats = monthlyData.models[entry.model]
    monthlyModelStats.totalRequests++
    monthlyModelStats.totalTokens += entry.tokenUsage.total
    monthlyModelStats.totalCost += entry.tokenUsage.cost || 0
    monthlyModelStats.avgResponseTime = ((monthlyModelStats.avgResponseTime * (monthlyModelStats.totalRequests - 1)) + entry.responseTime) / monthlyModelStats.totalRequests
    monthlyModelStats.lastUsed = entry.timestamp
    
    if (!entry.success) {
      monthlyModelStats.failureCount++
    }
    monthlyModelStats.successRate = ((monthlyModelStats.totalRequests - monthlyModelStats.failureCount) / monthlyModelStats.totalRequests) * 100
    
    // Check quota limits and create alerts
    const newAlerts = [...state.activeAlerts]
    if (state.quotaLimits.enabled && state.alertsEnabled) {
      // Daily limits
      const dailyLimits = state.quotaLimits.daily
      
      // Token alerts
      if (dailyData.totalTokens >= dailyLimits.tokens) {
        newAlerts.push(createAlert('exceeded', dailyLimits.tokens, dailyData.totalTokens, 'token', 'daily'))
      } else if (dailyData.totalTokens >= dailyLimits.tokens * 0.95) {
        newAlerts.push(createAlert('critical', dailyLimits.tokens, dailyData.totalTokens, 'token', 'daily'))
      } else if (dailyData.totalTokens >= dailyLimits.tokens * 0.8) {
        newAlerts.push(createAlert('warning', dailyLimits.tokens, dailyData.totalTokens, 'token', 'daily'))
      }
      
      // Request alerts
      if (dailyData.totalRequests >= dailyLimits.requests) {
        newAlerts.push(createAlert('exceeded', dailyLimits.requests, dailyData.totalRequests, 'request', 'daily'))
      } else if (dailyData.totalRequests >= dailyLimits.requests * 0.95) {
        newAlerts.push(createAlert('critical', dailyLimits.requests, dailyData.totalRequests, 'request', 'daily'))
      } else if (dailyData.totalRequests >= dailyLimits.requests * 0.8) {
        newAlerts.push(createAlert('warning', dailyLimits.requests, dailyData.totalRequests, 'request', 'daily'))
      }
      
      // Cost alerts
      if (dailyData.totalCost >= dailyLimits.cost) {
        newAlerts.push(createAlert('exceeded', dailyLimits.cost, dailyData.totalCost, 'cost', 'daily'))
      } else if (dailyData.totalCost >= dailyLimits.cost * 0.95) {
        newAlerts.push(createAlert('critical', dailyLimits.cost, dailyData.totalCost, 'cost', 'daily'))
      } else if (dailyData.totalCost >= dailyLimits.cost * 0.8) {
        newAlerts.push(createAlert('warning', dailyLimits.cost, dailyData.totalCost, 'cost', 'daily'))
      }
      
      // Monthly limits
      const monthlyLimits = state.quotaLimits.monthly
      
      // Monthly token alerts
      if (monthlyData.totalTokens >= monthlyLimits.tokens) {
        newAlerts.push(createAlert('exceeded', monthlyLimits.tokens, monthlyData.totalTokens, 'token', 'monthly'))
      } else if (monthlyData.totalTokens >= monthlyLimits.tokens * 0.95) {
        newAlerts.push(createAlert('critical', monthlyLimits.tokens, monthlyData.totalTokens, 'token', 'monthly'))
      } else if (monthlyData.totalTokens >= monthlyLimits.tokens * 0.8) {
        newAlerts.push(createAlert('warning', monthlyLimits.tokens, monthlyData.totalTokens, 'token', 'monthly'))
      }
      
      // Monthly request alerts
      if (monthlyData.totalRequests >= monthlyLimits.requests) {
        newAlerts.push(createAlert('exceeded', monthlyLimits.requests, monthlyData.totalRequests, 'request', 'monthly'))
      } else if (monthlyData.totalRequests >= monthlyLimits.requests * 0.95) {
        newAlerts.push(createAlert('critical', monthlyLimits.requests, monthlyData.totalRequests, 'request', 'monthly'))
      } else if (monthlyData.totalRequests >= monthlyLimits.requests * 0.8) {
        newAlerts.push(createAlert('warning', monthlyLimits.requests, monthlyData.totalRequests, 'request', 'monthly'))
      }
      
      // Monthly cost alerts
      if (monthlyData.totalCost >= monthlyLimits.cost) {
        newAlerts.push(createAlert('exceeded', monthlyLimits.cost, monthlyData.totalCost, 'cost', 'monthly'))
      } else if (monthlyData.totalCost >= monthlyLimits.cost * 0.95) {
        newAlerts.push(createAlert('critical', monthlyLimits.cost, monthlyData.totalCost, 'cost', 'monthly'))
      } else if (monthlyData.totalCost >= monthlyLimits.cost * 0.8) {
        newAlerts.push(createAlert('warning', monthlyLimits.cost, monthlyData.totalCost, 'cost', 'monthly'))
      }
    }
    
    // Update performance metrics
    const allModels = Object.values(dailyData.models)
    const avgSuccessRate = allModels.reduce((sum, model) => sum + model.successRate, 0) / allModels.length
    const avgResponseTime = allModels.reduce((sum, model) => sum + model.avgResponseTime, 0) / allModels.length
    const totalFailures = allModels.reduce((sum, model) => sum + model.failureCount, 0)
    
    const updatedPerformanceMetrics = {
      avgSuccessRate: isNaN(avgSuccessRate) ? 0 : avgSuccessRate,
      avgResponseTime: isNaN(avgResponseTime) ? 0 : avgResponseTime,
      totalFailures,
      lastCalculated: Date.now()
    }
    
    // Update store
    usageStore.set({
      ...state,
      currentSession: updatedSession,
      dailyUsage,
      monthlyUsage,
      activeAlerts: newAlerts,
      performanceMetrics: updatedPerformanceMetrics
    })
  },

  /**
   * Acknowledge an alert
   */
  acknowledgeAlert(alertId: string) {
    const state = usageStore.get()
    const updatedAlerts = state.activeAlerts.map(alert =>
      alert.id === alertId ? { ...alert, acknowledged: true } : alert
    )
    
    usageStore.set({
      ...state,
      activeAlerts: updatedAlerts
    })
  },

  /**
   * Clear acknowledged alerts
   */
  clearAcknowledgedAlerts() {
    const state = usageStore.get()
    const activeAlerts = state.activeAlerts.filter(alert => !alert.acknowledged)
    
    usageStore.set({
      ...state,
      activeAlerts
    })
  },

  /**
   * Update quota limits
   */
  updateQuotaLimits(limits: Partial<QuotaLimits>) {
    const state = usageStore.get()
    usageStore.set({
      ...state,
      quotaLimits: {
        ...state.quotaLimits,
        ...limits,
        daily: { ...state.quotaLimits.daily, ...limits.daily },
        monthly: { ...state.quotaLimits.monthly, ...limits.monthly }
      }
    })
  },

  /**
   * Enable/disable quota enforcement
   */
  toggleQuotaEnforcement(enabled: boolean) {
    const state = usageStore.get()
    usageStore.set({
      ...state,
      quotaLimits: {
        ...state.quotaLimits,
        enabled
      }
    })
  },

  /**
   * Enable/disable usage tracking
   */
  toggleTracking(enabled: boolean) {
    usageStore.set({
      ...usageStore.get(),
      trackingEnabled: enabled
    })
  },

  /**
   * Enable/disable alerts
   */
  toggleAlerts(enabled: boolean) {
    usageStore.set({
      ...usageStore.get(),
      alertsEnabled: enabled
    })
  },

  /**
   * Get usage summary for a specific period
   */
  getUsageSummary(period: 'today' | 'thisMonth' | 'all'): {
    requests: number
    tokens: number
    cost: number
    models: ModelUsageStats[]
    timeRange: { start: string, end: string }
  } {
    const state = usageStore.get()
    
    switch (period) {
      case 'today': {
        const today = getCurrentDateString()
        const dailyData = state.dailyUsage[today]
        return {
          requests: dailyData?.totalRequests || 0,
          tokens: dailyData?.totalTokens || 0,
          cost: dailyData?.totalCost || 0,
          models: dailyData ? Object.values(dailyData.models) : [],
          timeRange: { start: today, end: today }
        }
      }
      
      case 'thisMonth': {
        const thisMonth = getCurrentMonthString()
        const monthlyData = state.monthlyUsage[thisMonth]
        return {
          requests: monthlyData?.totalRequests || 0,
          tokens: monthlyData?.totalTokens || 0,
          cost: monthlyData?.totalCost || 0,
          models: monthlyData ? Object.values(monthlyData.models) : [],
          timeRange: { start: thisMonth + '-01', end: getCurrentDateString() }
        }
      }
      
      case 'all': {
        const allDaily = Object.values(state.dailyUsage)
        const totalRequests = allDaily.reduce((sum, day) => sum + day.totalRequests, 0)
        const totalTokens = allDaily.reduce((sum, day) => sum + day.totalTokens, 0)
        const totalCost = allDaily.reduce((sum, day) => sum + day.totalCost, 0)
        
        // Aggregate model stats
        const modelStats: Record<string, ModelUsageStats> = {}
        allDaily.forEach(day => {
          Object.values(day.models).forEach(model => {
            if (!modelStats[model.model]) {
              modelStats[model.model] = { ...model }
            } else {
              const existing = modelStats[model.model]
              existing.totalRequests += model.totalRequests
              existing.totalTokens += model.totalTokens
              existing.totalCost += model.totalCost
              existing.avgResponseTime = (existing.avgResponseTime + model.avgResponseTime) / 2
              existing.failureCount += model.failureCount
              existing.successRate = ((existing.totalRequests - existing.failureCount) / existing.totalRequests) * 100
              existing.lastUsed = Math.max(existing.lastUsed, model.lastUsed)
            }
          })
        })
        
        const dates = Object.keys(state.dailyUsage).sort()
        return {
          requests: totalRequests,
          tokens: totalTokens,
          cost: totalCost,
          models: Object.values(modelStats),
          timeRange: { 
            start: dates[0] || getCurrentDateString(), 
            end: dates[dates.length - 1] || getCurrentDateString() 
          }
        }
      }
      
      default:
        return {
          requests: 0,
          tokens: 0,
          cost: 0,
          models: [],
          timeRange: { start: '', end: '' }
        }
    }
  },

  /**
   * Clean up old usage data based on retention policy
   */
  cleanupOldData() {
    const state = usageStore.get()
    const retentionDate = new Date()
    retentionDate.setDate(retentionDate.getDate() - state.retentionDays)
    const cutoffDate = retentionDate.toISOString().slice(0, 10)
    
    // Clean daily usage
    const dailyUsage: Record<string, PeriodUsage> = {}
    Object.entries(state.dailyUsage).forEach(([date, data]) => {
      if (date >= cutoffDate) {
        dailyUsage[date] = data
      }
    })
    
    // Clean monthly usage (keep at least 12 months)
    const monthCutoff = new Date()
    monthCutoff.setMonth(monthCutoff.getMonth() - 12)
    const monthCutoffStr = monthCutoff.toISOString().slice(0, 7)
    
    const monthlyUsage: Record<string, PeriodUsage> = {}
    Object.entries(state.monthlyUsage).forEach(([month, data]) => {
      if (month >= monthCutoffStr) {
        monthlyUsage[month] = data
      }
    })
    
    // Clean old alerts (older than 30 days)
    const alertCutoff = Date.now() - (30 * 24 * 60 * 60 * 1000)
    const activeAlerts = state.activeAlerts.filter(alert => 
      alert.timestamp > alertCutoff || !alert.acknowledged
    )
    
    usageStore.set({
      ...state,
      dailyUsage,
      monthlyUsage,
      activeAlerts
    })
  },

  /**
   * Export all usage data
   */
  exportData() {
    return {
      ...usageStore.get(),
      exportedAt: new Date().toISOString(),
      version: '1.0'
    }
  },

  /**
   * Reset all usage data
   */
  resetAllData() {
    usageStore.set({
      ...defaultUsageState,
      currentSession: {
        startTime: Date.now(),
        requests: 0,
        tokens: 0,
        cost: 0
      }
    })
  }
}

// Subscribe to storage events for cross-window sync
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === 'korean-grammar:usage' && e.newValue) {
      try {
        const data = JSON.parse(e.newValue)
        usageStore.set(data)
      } catch {
        // Ignore parsing errors
      }
    }
  })
}

// Auto cleanup on app start
if (typeof window !== 'undefined') {
  // Run cleanup once on app start
  setTimeout(() => {
    usageActions.cleanupOldData()
  }, 1000)
  
  // Schedule daily cleanup
  const now = new Date()
  const tomorrow = new Date(now)
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(1, 0, 0, 0) // 1 AM next day
  
  const msUntilTomorrow = tomorrow.getTime() - now.getTime()
  setTimeout(() => {
    usageActions.cleanupOldData()
    // Then run daily
    setInterval(() => {
      usageActions.cleanupOldData()
    }, 24 * 60 * 60 * 1000)
  }, msUntilTomorrow)
}

// Helper functions for components
export function getCurrentUsage() {
  return usageStore.get()
}

export function getActiveAlerts(): UsageAlert[] {
  return usageStore.get().activeAlerts.filter(alert => !alert.acknowledged)
}

export function formatUsageNumber(value: number, type: 'tokens' | 'requests' | 'cost'): string {
  switch (type) {
    case 'tokens':
      if (value < 1000) return value.toString()
      if (value < 1000000) return `${(value / 1000).toFixed(1)}K`
      return `${(value / 1000000).toFixed(1)}M`
    
    case 'requests':
      return value.toString()
    
    case 'cost':
      if (value < 0.01) return `$${(value * 1000).toFixed(2)}‰`
      return `$${value.toFixed(4)}`
    
    default:
      return value.toString()
  }
}